/**
 * Work Queue — one place for everything due.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE 2026-09-08 REWRITE
 *
 * Taylor asked three things of this page, and answering them turned it from a
 * read-only list into the module's single work surface:
 *
 * 1. "in the work queue does this right now connect to anythign in hotel
 *    supllier management?"
 *
 *    It does, and always did — the connection was just invisible. /api/work-queue
 *    has two producers: CRM follow-ups and `hsp.renewal_task` joined to the hotel
 *    property. The page rendered renewals in the same grey as everything else, so
 *    there was no way to tell. Renewals now carry the property and vendor family
 *    on the row and link into the Renewals workspace, and the header says which
 *    systems feed the queue.
 *
 * 2. "I also as an admin be able to see everyones work queue and then filter on
 *    people and that should be permissioned like admins and people we say can
 *    see more then just their own work queue."
 *
 *    The "Everyone" pill used to just drop `assigned_to` from the request, and
 *    the server never checked whether the caller was allowed to see other
 *    people's work — so every csm and every READ-ONLY VIEWER could list every
 *    customer's follow-ups. The gate is now server-side
 *    (view:team_work_queue, admin + manager), this page renders the Team scope
 *    and the person picker only when /api/work-queue says `canSeeTeam`, and a
 *    403 is surfaced rather than swallowed into an empty list.
 *
 * 3. "the ability to share stuff in your work queue with other users and assign
 *    it to them but still see it."
 *
 *    Reassigning alone moves an item OUT of your queue, which is why people stop
 *    reassigning and things sit. Every item now arrives tagged with how you
 *    relate to it — assigned / shared with me / shared by me — so a handoff
 *    stays visible to the person who made it.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT ALSO ABSORBS THE FOLLOW-UPS PAGE
 *
 * Taylor: "In follow ups I don't think this is needed seems liek work queue is
 * already doing this." Correct — follow-ups were producer 1 of this queue, so
 * /crm/followups was a strictly narrower view of this screen. The one thing it
 * had that this did not was CREATE and EDIT, so those moved here before it was
 * deleted; the API already supported both.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { MultiSelect } from '@/features/crm/components/MultiSelect';
import { fmtCalendarDate } from '@/features/crm/lib/dates';

type WorkType = 'followup' | 'renewal';
type Scope = 'mine' | 'shared' | 'team';
type Relation = 'assigned' | 'shared_with_me' | 'shared_by_me';

interface ShareInfo {
  sharedBy: string;
  sharedWith: string;
  note: string | null;
  reassigned: boolean;
  previousAssignee: string | null;
  at: string;
}

interface WorkItem {
  id: string;
  type: WorkType;
  title: string;
  customerId: string;
  dueAt: string;
  assignee: string | null;
  status: 'open' | 'completed';
  link: string;
  meta: string | null;
  completable: boolean;
  relation: Relation | null;
  shares: ShareInfo[];
}

interface CustomerLite {
  customer_id: string;
  name: string;
}

const TYPE_LABEL: Record<WorkType, string> = {
  followup: 'Follow-up',
  renewal: 'Renewal',
};

const RELATION_LABEL: Record<Relation, string> = {
  assigned: 'Yours',
  shared_with_me: 'Shared with you',
  shared_by_me: 'You handed off',
};

export default function WorkQueuePage() {
  const [items, setItems] = useState<WorkItem[]>([]);
  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  const [assignees, setAssignees] = useState<
    Array<{ email: string; open: number }>
  >([]);
  const [canSeeTeam, setCanSeeTeam] = useState(false);
  const [scope, setScope] = useState<Scope>('mine');
  const [people, setPeople] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<'all' | WorkType>('all');
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** Which item's share panel is open, and which is being edited. */
  const [sharing, setSharing] = useState<WorkItem | null>(null);
  const [editing, setEditing] = useState<WorkItem | null>(null);
  const [creating, setCreating] = useState(false);

  const { me: identity, can } = useMe();
  const me = (identity?.email ?? '').toLowerCase();
  const canWrite = can?.('write:followup') ?? false;

  const customerNames = useMemo(
    () => new Map(customers.map((c) => [c.customer_id, c.name])),
    [customers],
  );

  const load = useCallback(async () => {
    if (!me) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    params.set('scope', scope);
    /**
     * ONE person at a time on the wire. The endpoint gates `assigned_to` on the
     * same capability as team scope, so this cannot be used to widen access —
     * and for a multi-person view we ask for the team and filter client-side,
     * which keeps the server contract simple and the permission single-purpose.
     */
    if (scope === 'team' && people.length === 1) {
      params.set('assigned_to', people[0]);
    }
    if (typeFilter !== 'all') params.set('type', typeFilter);
    if (includeCompleted) params.set('include_completed', '1');

    try {
      const [wq, ct] = await Promise.all([
        apiFetch<{
          items: WorkItem[];
          canSeeTeam: boolean;
          assignees: Array<{ email: string; open: number }>;
        }>(`/api/work-queue?${params.toString()}`),
        apiFetch<{ customers: CustomerLite[] }>('/api/customers?limit=5000'),
      ]);
      setItems(wq.items);
      setCanSeeTeam(wq.canSeeTeam);
      setAssignees(wq.assignees ?? []);
      setCustomers(ct.customers);
    } catch (e) {
      /**
       * A 403 from the scope gate is SHOWN. The old page could not produce this
       * because there was no gate; the failure mode to avoid now is silently
       * falling back to `mine`, which looks identical to "you have no work" and
       * is how a permission problem hides for months.
       */
      setError(e instanceof ApiError ? e.message : String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [me, scope, people, typeFilter, includeCompleted]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Client-side narrowing for a multi-person team view. */
  const visible = useMemo(() => {
    if (scope !== 'team' || people.length <= 1) return items;
    const set = new Set(people.map((p) => p.toLowerCase()));
    return items.filter((i) => i.assignee && set.has(i.assignee.toLowerCase()));
  }, [items, scope, people]);

  const toggleComplete = async (item: WorkItem) => {
    if (!item.completable) return;
    const rawId = item.id.split(':')[1];
    const completed = item.status !== 'completed';
    const prev = items;
    setItems(
      items.map((x) =>
        x.id === item.id
          ? { ...x, status: completed ? 'completed' : 'open' }
          : x,
      ),
    );
    try {
      await apiFetch(`/api/followups/${rawId}`, {
        method: 'PATCH',
        body: JSON.stringify({ completed }),
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setItems(prev);
    }
  };

  const buckets = useMemo(() => bucketItems(visible), [visible]);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Work Queue</h1>
        <p className="page-intro">
          Everything due across the team, from every system that produces work —{' '}
          <strong>CRM follow-ups</strong> and{' '}
          <strong>hotel-rate renewals</strong> today, with rate opportunities
          and business reviews joining the same queue as they come online.
          Overdue stays pinned at the top.
        </p>
      </div>

      <div className="kpi-row kpi-row-4">
        <div className="kpi-card kpi-red">
          <span className="kpi-label">Overdue</span>
          <span className="kpi-value">{buckets.overdue.length}</span>
          <span className="kpi-sub">past their due date</span>
        </div>
        <div className="kpi-card kpi-amber">
          <span className="kpi-label">Due today</span>
          <span className="kpi-value">{buckets.today.length}</span>
          <span className="kpi-sub">action expected today</span>
        </div>
        <div className="kpi-card kpi-highlight">
          <span className="kpi-label">This week</span>
          <span className="kpi-value">{buckets.thisWeek.length}</span>
          <span className="kpi-sub">within 7 days</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Later</span>
          <span className="kpi-value">{buckets.later.length}</span>
          <span className="kpi-sub">scheduled out</span>
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <span className="section-label">Queue</span>
          <span className="section-meta">
            {loading ? 'Refreshing…' : `${visible.length} items`}
          </span>
        </div>
        <div className="section-body">
          <div className="filter-bar">
            <span className="field-label-inline">Show</span>
            <button
              className={'pill' + (scope === 'mine' ? ' active' : '')}
              data-testid="scope-mine"
              onClick={() => setScope('mine')}
              type="button"
            >
              Assigned to me
            </button>
            <button
              className={'pill' + (scope === 'shared' ? ' active' : '')}
              data-testid="scope-shared"
              onClick={() => setScope('shared')}
              type="button"
            >
              Shared with me
            </button>
            {/* THE GATE IS THE SERVER'S; this only decides whether to draw the
                control. Rendering it for someone who cannot use it produces a
                403 they cannot act on, which reads as a broken page. */}
            {canSeeTeam ? (
              <button
                className={'pill' + (scope === 'team' ? ' active' : '')}
                data-testid="scope-team"
                onClick={() => setScope('team')}
                type="button"
              >
                Everyone
              </button>
            ) : null}

            {canSeeTeam && scope === 'team' ? (
              <>
                <span className="field-label-inline" style={{ marginLeft: 12 }}>
                  People
                </span>
                <MultiSelect
                  onChange={setPeople}
                  options={assignees.map((a) => a.email)}
                  placeholder="Everyone"
                  value={people}
                />
              </>
            ) : null}

            <span className="field-label-inline" style={{ marginLeft: 12 }}>
              Type
            </span>
            {(['all', 'followup', 'renewal'] as const).map((t) => (
              <button
                className={'pill' + (typeFilter === t ? ' active' : '')}
                key={t}
                onClick={() => setTypeFilter(t)}
                type="button"
              >
                {t === 'all' ? 'All' : TYPE_LABEL[t] + 's'}
              </button>
            ))}
            <button
              className={'pill' + (includeCompleted ? ' active' : '')}
              onClick={() => setIncludeCompleted(!includeCompleted)}
              style={{ marginLeft: 12 }}
              type="button"
            >
              Include completed
            </button>

            {/* CREATE — the one thing the retired Follow-ups page had that this
                screen did not. */}
            {canWrite ? (
              <button
                className="btn btn-primary"
                data-testid="new-followup"
                onClick={() => setCreating(true)}
                style={{ marginLeft: 'auto' }}
                type="button"
              >
                New follow-up
              </button>
            ) : null}
          </div>

          {error ? <div className="banner banner-error">{error}</div> : null}
          {notice ? <div className="banner banner-info">{notice}</div> : null}

          <QueueBucket
            customers={customerNames}
            items={buckets.overdue}
            me={me}
            onEdit={canWrite ? setEditing : undefined}
            onShare={canWrite ? setSharing : undefined}
            onToggle={toggleComplete}
            title="Overdue"
            tone="red"
          />
          <QueueBucket
            customers={customerNames}
            items={buckets.today}
            me={me}
            onEdit={canWrite ? setEditing : undefined}
            onShare={canWrite ? setSharing : undefined}
            onToggle={toggleComplete}
            title="Due today"
            tone="amber"
          />
          <QueueBucket
            customers={customerNames}
            items={buckets.thisWeek}
            me={me}
            onEdit={canWrite ? setEditing : undefined}
            onShare={canWrite ? setSharing : undefined}
            onToggle={toggleComplete}
            title="This week"
            tone="default"
          />
          <QueueBucket
            customers={customerNames}
            items={buckets.later}
            me={me}
            onEdit={canWrite ? setEditing : undefined}
            onShare={canWrite ? setSharing : undefined}
            onToggle={toggleComplete}
            title="Later"
            tone="default"
          />
          {includeCompleted ? (
            <QueueBucket
              completed
              customers={customerNames}
              items={buckets.done}
              me={me}
              onToggle={toggleComplete}
              title="Completed"
              tone="green"
            />
          ) : null}

          {!loading && visible.length === 0 && !error ? (
            <div className="empty-state">
              <div className="glyph">✅</div>
              <div className="empty-head">Nothing due in this view</div>
              <div className="empty-sub">
                {scope === 'shared'
                  ? 'Nobody has shared an item with you.'
                  : scope === 'team'
                    ? 'Nothing open for the selected people.'
                    : 'You are caught up. Create a follow-up above, or check what has been shared with you.'}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {creating ? (
        <FollowupForm
          customers={customers}
          me={me}
          onClose={() => setCreating(false)}
          onSaved={(msg) => {
            setNotice(msg);
            setCreating(false);
            void load();
          }}
        />
      ) : null}

      {editing ? (
        <FollowupForm
          customers={customers}
          item={editing}
          me={me}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            setNotice(msg);
            setEditing(null);
            void load();
          }}
        />
      ) : null}

      {sharing ? (
        <SharePanel
          assignees={assignees.map((a) => a.email)}
          item={sharing}
          me={me}
          onClose={() => setSharing(null)}
          onSaved={(msg) => {
            setNotice(msg);
            setSharing(null);
            void load();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Bucket by CALENDAR DAY, in UTC, for the same reason fmtDate does.
 *
 * This compared `new Date(it.dueAt)` — UTC midnight — against a LOCAL `now`, so
 * in Denver an item due today was already "overdue" from midnight UTC, i.e. six
 * hours before the day it is due even began. Same root cause as the off-by-a-day
 * label: a calendar date being treated as an instant.
 *
 * Comparing day NUMBERS rather than timestamps removes the class of bug
 * entirely — there is no hour left for a timezone to shift.
 */
function utcDayNumber(d: Date): number {
  return Math.floor(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86_400_000,
  );
}

function bucketItems(items: WorkItem[]) {
  const overdue: WorkItem[] = [];
  const today: WorkItem[] = [];
  const thisWeek: WorkItem[] = [];
  const later: WorkItem[] = [];
  const done: WorkItem[] = [];

  const todayDay = utcDayNumber(new Date());
  for (const it of items) {
    if (it.status === 'completed') {
      done.push(it);
      continue;
    }
    const due = new Date(it.dueAt);
    if (Number.isNaN(due.getTime())) {
      // An unparseable due date must not silently sort as "overdue" — that is
      // a data problem masquerading as urgent work.
      later.push(it);
      continue;
    }
    const dueDay = utcDayNumber(due);
    if (dueDay < todayDay) overdue.push(it);
    else if (dueDay === todayDay) today.push(it);
    else if (dueDay <= todayDay + 7) thisWeek.push(it);
    else later.push(it);
  }
  return { overdue, today, thisWeek, later, done };
}

function QueueBucket({
  title,
  items,
  customers,
  me,
  onToggle,
  onShare,
  onEdit,
  tone,
  completed,
}: {
  title: string;
  items: WorkItem[];
  customers: Map<string, string>;
  me: string;
  onToggle: (it: WorkItem) => void;
  onShare?: (it: WorkItem) => void;
  onEdit?: (it: WorkItem) => void;
  tone: 'red' | 'amber' | 'green' | 'default';
  completed?: boolean;
}) {
  if (items.length === 0) return null;

  return (
    <div className="queue-bucket">
      <div className={`queue-bucket-head queue-tone-${tone}`}>
        {title}
        <span className="queue-bucket-count">{items.length}</span>
      </div>
      <ul className="queue-list">
        {items.map((it) => (
          <QueueRow
            completed={completed}
            customers={customers}
            item={it}
            key={it.id}
            me={me}
            onEdit={onEdit}
            onShare={onShare}
            onToggle={onToggle}
          />
        ))}
      </ul>
    </div>
  );
}

function QueueRow({
  item,
  customers,
  me,
  onToggle,
  onShare,
  onEdit,
  completed,
}: {
  item: WorkItem;
  customers: Map<string, string>;
  me: string;
  onToggle: (it: WorkItem) => void;
  onShare?: (it: WorkItem) => void;
  onEdit?: (it: WorkItem) => void;
  completed?: boolean;
}) {
  const isDone = item.status === 'completed';
  const isFollowup = item.type === 'followup';

  return (
    <li className={'queue-row' + (completed ? ' queue-row-done' : '')}>
      {item.completable ? (
        <input
          aria-label={`Mark "${item.title}" complete`}
          checked={isDone}
          className="queue-check"
          onChange={() => onToggle(item)}
          type="checkbox"
        />
      ) : (
        <span
          className="queue-check queue-check-external"
          title="Worked and closed in the Renewals workspace"
        >
          ↗
        </span>
      )}

      <div className="queue-body">
        <div className="queue-title-row">
          <TypeBadge type={item.type} />
          {/* HOW YOU RELATE TO IT. The whole point of shares: without this a
              handed-off item is indistinguishable from one still yours. */}
          {item.relation ? (
            <span className={`queue-relation queue-relation-${item.relation}`}>
              {RELATION_LABEL[item.relation]}
            </span>
          ) : null}
          <span className={'queue-title' + (isDone ? ' queue-title-done' : '')}>
            {item.title}
          </span>
        </div>

        <div className="queue-meta">
          <Link
            className="link"
            to={`/crm/customers/${encodeURIComponent(item.customerId)}`}
          >
            {customers.get(item.customerId) || item.customerId}
          </Link>
          <span>Due {fmtDate(item.dueAt)}</span>
          {item.assignee ? (
            <span>
              {item.assignee.toLowerCase() === me
                ? 'You'
                : `For ${item.assignee}`}
            </span>
          ) : (
            <span className="queue-unassigned">Unassigned</span>
          )}
          {/* Renewals: say which property and chain, and go where it is worked.
              This is the hotel-supplier connection Taylor could not see. */}
          {item.meta ? <span>{item.meta}</span> : null}
          {item.type === 'renewal' ? (
            <a className="link" href={item.link}>
              Open in Renewals →
            </a>
          ) : null}
        </div>

        {/* Share provenance, in words. "Dana handed this to you" is the fact
            somebody needs; a bare "shared" badge is not. */}
        {item.shares.length > 0 ? (
          <div className="queue-shares">
            {item.shares.map((s) => (
              <div
                className="queue-share"
                key={`${s.sharedBy}->${s.sharedWith}`}
              >
                {describeShare(s, me)}
                {s.note ? (
                  <span className="queue-share-note">“{s.note}”</span>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="queue-actions">
        {onEdit && isFollowup && !isDone ? (
          <button className="pill" onClick={() => onEdit(item)} type="button">
            Edit
          </button>
        ) : null}
        {onShare && !isDone ? (
          <button
            className="pill"
            data-testid={`share-${item.id}`}
            onClick={() => onShare(item)}
            type="button"
          >
            Share
          </button>
        ) : null}
      </div>
    </li>
  );
}

function describeShare(s: ShareInfo, me: string): string {
  const byMe = s.sharedBy.toLowerCase() === me;
  const toMe = s.sharedWith.toLowerCase() === me;
  const verb = s.reassigned ? 'handed this to' : 'looped in';
  if (byMe) return `You ${verb} ${s.sharedWith}`;
  if (toMe) return `${s.sharedBy} ${verb} you`;
  return `${s.sharedBy} ${verb} ${s.sharedWith}`;
}

/**
 * Create or edit a follow-up.
 *
 * Absorbed from the retired /crm/followups page. The API already had POST
 * /api/followups and PATCH /api/followups/[id] with note, due_at and
 * assigned_to, so this is a form over endpoints that existed — which is exactly
 * why that page was safe to delete rather than needing a replacement built.
 */
function FollowupForm({
  item,
  customers,
  me,
  onClose,
  onSaved,
}: {
  item?: WorkItem;
  customers: CustomerLite[];
  me: string;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const editing = !!item;
  const [customerId, setCustomerId] = useState(item?.customerId ?? '');
  const [note, setNote] = useState(item?.title ?? '');
  const [dueAt, setDueAt] = useState(
    (item?.dueAt ?? new Date().toISOString()).slice(0, 10),
  );
  const [assignedTo, setAssignedTo] = useState(item?.assignee ?? me);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!customerId || !note.trim() || !dueAt) {
      setErr('Customer, note and due date are all required.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      if (editing) {
        const rawId = item!.id.split(':')[1];
        await apiFetch(`/api/followups/${rawId}`, {
          method: 'PATCH',
          body: JSON.stringify({
            note: note.trim(),
            due_at: new Date(dueAt).toISOString(),
            assigned_to: assignedTo,
          }),
        });
        onSaved('Follow-up updated.');
      } else {
        await apiFetch('/api/followups', {
          method: 'POST',
          body: JSON.stringify({
            customer_id: customerId,
            note: note.trim(),
            due_at: new Date(dueAt).toISOString(),
            assigned_to: assignedTo,
          }),
        });
        onSaved('Follow-up created.');
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dialog-backdrop" onClick={onClose} role="presentation">
      <div
        aria-label={editing ? 'Edit follow-up' : 'New follow-up'}
        className="dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <h2 className="dialog-title">
          {editing ? 'Edit follow-up' : 'New follow-up'}
        </h2>

        {err ? <div className="banner banner-error">{err}</div> : null}

        <label className="field">
          <span className="field-label">Customer</span>
          {editing ? (
            // The owning customer is not editable: moving a follow-up between
            // accounts would silently rewrite one account's history into
            // another's. Delete and recreate is the honest path.
            <input
              disabled
              readOnly
              value={
                customers.find((c) => c.customer_id === customerId)?.name ??
                customerId
              }
            />
          ) : (
            <select
              onChange={(e) => setCustomerId(e.target.value)}
              value={customerId}
            >
              <option value="">Select a customer…</option>
              {customers.map((c) => (
                <option key={c.customer_id} value={c.customer_id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </label>

        <label className="field">
          <span className="field-label">What needs doing</span>
          <textarea
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            value={note}
          />
        </label>

        <label className="field">
          <span className="field-label">Due</span>
          <input
            onChange={(e) => setDueAt(e.target.value)}
            type="date"
            value={dueAt}
          />
        </label>

        <label className="field">
          <span className="field-label">Assigned to</span>
          <input
            onChange={(e) => setAssignedTo(e.target.value)}
            placeholder="name@cbtravel.com"
            type="email"
            value={assignedTo}
          />
        </label>

        <div className="dialog-actions">
          <button className="btn" onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={saving}
            onClick={submit}
            type="button"
          >
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Share or hand off one item.
 *
 * TWO DISTINCT ACTIONS behind one panel, and the difference is spelled out on
 * screen rather than left to a checkbox label:
 *
 *   Loop in  — they see it, ownership does not move.
 *   Hand off — ownership moves AND you keep seeing it. That second half is the
 *              feature; reassignment on its own already existed and is why
 *              people stopped reassigning.
 */
function SharePanel({
  item,
  assignees,
  me,
  onClose,
  onSaved,
}: {
  item: WorkItem;
  assignees: string[];
  me: string;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const [withWhom, setWithWhom] = useState('');
  const [note, setNote] = useState('');
  const [reassign, setReassign] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    const target = withWhom.trim().toLowerCase();
    if (!target || !target.includes('@')) {
      setErr('Enter the email address of the person to share with.');
      return;
    }
    if (target === me) {
      setErr('You already see your own items.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await apiFetch('/api/work-queue/share', {
        method: 'POST',
        body: JSON.stringify({
          work_item_id: item.id,
          shared_with: target,
          note: note.trim() || undefined,
          reassign,
        }),
      });
      onSaved(
        reassign
          ? `Handed off to ${target}. It stays in your queue under “You handed off”.`
          : `${target} can now see this. It is still assigned to you.`,
      );
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dialog-backdrop" onClick={onClose} role="presentation">
      <div
        aria-label="Share work item"
        className="dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <h2 className="dialog-title">Share this item</h2>
        <p className="page-intro" style={{ marginTop: 0 }}>
          {item.title}
        </p>

        {err ? <div className="banner banner-error">{err}</div> : null}

        <label className="field">
          <span className="field-label">Share with</span>
          <input
            list="wq-assignees"
            onChange={(e) => setWithWhom(e.target.value)}
            placeholder="name@cbtravel.com"
            type="email"
            value={withWhom}
          />
          <datalist id="wq-assignees">
            {assignees.map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>
        </label>

        <label className="field">
          <span className="field-label">Note (optional)</span>
          <input
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why you're sharing it"
            value={note}
          />
        </label>

        <fieldset className="field share-mode">
          <legend className="field-label">What should happen</legend>
          <label className="share-option">
            <input
              checked={!reassign}
              name="share-mode"
              onChange={() => setReassign(false)}
              type="radio"
            />
            <span>
              <strong>Loop them in.</strong> They can see it; it stays assigned
              to you.
            </span>
          </label>
          <label className="share-option">
            <input
              checked={reassign}
              name="share-mode"
              onChange={() => setReassign(true)}
              type="radio"
            />
            <span>
              <strong>Hand it off.</strong> It becomes theirs — and stays in
              your queue as “You handed off”, so you can still follow it.
            </span>
          </label>
        </fieldset>

        <div className="dialog-actions">
          <button className="btn" onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={saving}
            onClick={submit}
            type="button"
          >
            {saving ? 'Sharing…' : reassign ? 'Hand off' : 'Share'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TypeBadge({ type }: { type: WorkType }) {
  return (
    <span className={`queue-type queue-type-${type}`}>{TYPE_LABEL[type]}</span>
  );
}

/**
 * A DUE DATE IS A CALENDAR DAY, NOT AN INSTANT.
 *
 * This rendered `new Date(iso).toLocaleDateString()`, i.e. the reader's local
 * timezone — so a task due 2026-09-02, stored as UTC midnight, displayed as
 * "Sep 1, 2026" for everyone west of UTC. That is the whole company. Caught by
 * rendering the page in the dev-mock harness and reading the row, not by any
 * test: the string was well-formed, just a day out.
 *
 * lib/dates.ts already exists for exactly this and documents the trap in full;
 * this page simply had its own formatter and got it backwards.
 *
 * ⚠️  THE TWO PRODUCERS DISAGREE IN THE SCHEMA, and this is a latent
 * inconsistency worth naming rather than papering over:
 *
 *   hsp.renewal_task.due_by      DateTime @db.Date  — a true calendar date
 *   crm.customer_followups.due_at DateTime          — an instant
 *
 * Both are AUTHORED as calendar days (this page's form and the retired
 * Follow-ups page both write UTC midnight from an `<input type="date">`), so
 * UTC is right for both today. But if anything ever writes a real time-of-day
 * into `due_at`, this will render its UTC day rather than the reader's. The
 * durable fix is `@db.Date` on due_at to match the other producer; that is a
 * migration and a separate change.
 */
function fmtDate(iso: string): string {
  return fmtCalendarDate(iso) ?? '—';
}
